import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

type SignatureType = 'sxsc' | 'hxsc';
type SignaturePower = 'dormant' | 'active' | 'supercharged';

interface SignatureProps {
  type: SignatureType;
  power?: SignaturePower;
  onClick?: () => void;
  className?: string;
  showEffects?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const getSignatureColors = (type: SignatureType) => {
  return type === 'sxsc' 
    ? ['from-sc-mint', 'to-sc-violet', 'text-sc-mint'] 
    : ['from-sc-blue', 'to-sc-mint', 'text-sc-blue'];
};

export const Signature: React.FC<SignatureProps> = React.memo(({ 
  type,
  power = 'dormant',
  onClick,
  className = '',
  showEffects = true,
  size = 'md'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Check if the signature is in the navbar or owner page
  const isOwnerPage = location.pathname === '/owner';
  const isInNavbar = className?.includes('navbar');
  
  const colors = getSignatureColors(type);

  const sizeClasses = {
    sm: 'text-sm px-2 py-1',
    md: 'text-base px-3 py-1.5',
    lg: 'text-lg px-4 py-2'
  };

  const handleClick = useCallback(() => {
    const targetPath = type === 'hxsc' ? '/hxsc-portal' : '/sxsc-portal';
    if (isOwnerPage && !isInNavbar) {
      setIsTransitioning(true);
      // Longer transition on owner page
      setTimeout(() => {
        navigate(targetPath);
      }, 1500);
    } else {
      // Instant navigation on other pages
      navigate(targetPath);
    }
  }, [navigate, isOwnerPage, type]);

  return (
    <motion.div
      className={`relative inline-flex items-center justify-center ${className}`}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.05 }}
      onClick={handleClick}
    >
      <motion.div
        className={`
          rounded-full 
          bg-gradient-to-r ${colors[0]} ${colors[1]}
          ${sizeClasses[size]}
          cursor-pointer
          font-bold
          backdrop-blur-sm
          border border-white/10
          shadow-lg
        `}
      >
        {type}
        {showEffects && (
          <Sparkles className="inline ml-1 w-4 h-4" />
        )}
      </motion.div>

      {/* Portal Effect - Only on owner page */}
      {isOwnerPage && (
        <AnimatePresence>
          {isTransitioning && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className={`w-32 h-32 bg-gradient-to-r ${colors[0]} ${colors[1]}`}
                initial={{ scale: 0, borderRadius: '9999px' }}
                animate={{ 
                  scale: [1, 5, 20],
                  borderRadius: ['9999px', '100px', '0px']
                }}
                transition={{
                  duration: 1.5,
                  ease: [0.4, 0, 0.2, 1]
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Hover Effect */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            className="absolute -inset-2 bg-gradient-to-r from-sc-blue/20 via-sc-mint/20 to-sc-violet/20 rounded-full blur-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
});

// Combined Signature Component
interface CombinedSignatureProps {
  className?: string;
}

export const CombinedSignature: React.FC<CombinedSignatureProps> = ({ 
  className = ''
}) => {
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <Signature type="sxsc" size="sm" />
      <motion.div
        className="w-8 h-8 rounded-full bg-gradient-to-r from-sc-blue via-sc-mint to-sc-violet"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.1, 1]
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <Signature type="hxsc" size="sm" />
    </div>
  );
};

// Signature Context
export const SignatureContext = React.createContext<{
  power: SignaturePower;
  setPower: (power: SignaturePower) => void;
}>({
  power: 'dormant',
  setPower: () => {},
});

export const useSignature = () => React.useContext(SignatureContext);

export const SignatureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [power, setPower] = useState<SignaturePower>('dormant');

  return (
    <SignatureContext.Provider value={{ power, setPower }}>
      {children}
    </SignatureContext.Provider>
  );
};
